import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { useAppSelector, useAppDispatch } from "store/index";
import {
  deleteLoyaltyLevel,
  getLoyaltyLevelsListList,
  handlePaginate,
  handleSelectAll,
  handleSelectItem,
  updateLoyaltyLevelsBunch,
} from "store/liveOps/loyalty/levels/slice";
import { loyaltyListLimits } from "configs/limits";
import { ModalTypes } from "configs/types";
import { openModal } from "store/modals/slice";

import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import Pagination from "components/Pagination";
import ActionBtn from "components/buttons/ActionBtn";
import LevelsFilter from "./components/LevelsFilter";
import LoyaltyRewardTypeLabels from "./components/LoyaltyRewardTypeLabels";

import {
  NotificationTypes,
  showNotification,
} from "../../../../store/notifications/slice";
import { Table } from "components/table/Table";
import EmptyState from "components/EmptyState/EmptyState";
import DataTableSkeleton from "components/global/Skeleton/TableSkeleton/DataTableSkeleton";
import PaginationSkeleton from "components/global/Skeleton/PaginationSkeleton/PaginationSkeleton";
import { usePermissionHelper } from "helpers/usePermissionHelper";
import { useRefreshList } from "helpers/useRefreshList";
import { RefreshButton } from "components/buttons/RefreshButton";

const LevelsList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const permissions = usePermissionHelper();
  const canCreate = permissions?.create ?? false;
  const canUpdate = permissions?.update ?? false;
  const canDelete = permissions?.delete ?? false;
  const canRead = permissions?.read ?? false;
  const activeLevelId = searchParams.get("entityId");

  const [order, setOrder] = useState<{
    title: string;
    isActive: string;
    valueToComplete: string;
    levelNumber: string;
  }>({
    title: "none",
    isActive: "none",
    valueToComplete: "none",
    levelNumber: "none",
  });
  const [currentLimit, setLimit] = useState<number>(loyaltyListLimits[0]);
  const [search, setSearch] = useState<string>("");
  const [level, setLevel] = useState<string>("");
  const [active, setActive] = useState<
    { value: number | string; label: string }[]
  >([
    {
      value: 3,
      label: t("all"),
    },
  ]);
  const options = [
    {
      label: t("activate"),
      value: "activate",
    },
    {
      label: t("hide"),
      value: "hide",
    },
  ];

  const list = useAppSelector((store) => store["loyalty-levels"].list);
  const isListFetched = useAppSelector(
    (store) => store["loyalty-levels"].isListFetched,
  );
  const totalItems = useAppSelector(
    (store) => store["loyalty-levels"].totalItems,
  );
  const totalPages = useAppSelector(
    (store) => store["loyalty-levels"].totalPages,
  );
  const activePage = useAppSelector(
    (store) => store["loyalty-levels"].activePage,
  );
  const selectedItems = useAppSelector(
    (store) => store["loyalty-levels"].selectedItems,
  );

  const processingItemId = useAppSelector(
    (store) => store["loyalty-levels"].processingItemId,
  );

  const { refresh, isRefreshRequested } = useRefreshList({
    sliceName: "loyalty-levels",
    isListFetched,
  });

  useEffect(() => {
    if (!isListFetched) {
      dispatch(
        getLoyaltyLevelsListList({
          limit: currentLimit,
          offset: activePage - 1,
          isActive:
            active[0] && active[0].value !== 3
              ? (active[0].value as number)
              : undefined,
          title: search,
          level: level,
          order,
        }),
      );
    }
  }, [isListFetched, activePage, currentLimit, order]);

  const handleAllClick = () => {
    dispatch(handleSelectAll());
  };
  const handleItemClick = (id: number) => {
    dispatch(handleSelectItem(id));
  };
  const handlePrevClick = () => {
    if (activePage > 1) {
      dispatch(handlePaginate(activePage - 1));
    }
  };
  const handleNextClick = () => {
    if (activePage !== totalPages) {
      dispatch(handlePaginate(activePage + 1));
    }
  };
  const handleFilterSearch = (s: string) => {
    setSearch(s);
  };
  const handleApply = () => {
    dispatch(handlePaginate(1));
  };

  const handleActionClick = async (e: string) => {
    if (!canUpdate) return;
    try {
      await dispatch(
        updateLoyaltyLevelsBunch({
          ids: selectedItems,
          isActive: e === "activate" ? 1 : 0,
        }),
      );
    } catch (error) {
      console.log(error, "error");
    }
  };

  const handleOpenEditModal = (id: number) => {
    navigate(
      `${location.pathname}?activeModal=${ModalTypes.loyaltyLevel}&entityId=${id}`,
    );
    dispatch(openModal({ activeModal: ModalTypes.loyaltyLevel }));
  };
  const handleOpenCreateModal = () => {
    navigate(`${location.pathname}?activeModal=${ModalTypes.loyaltyLevel}`);
    dispatch(openModal({ activeModal: ModalTypes.loyaltyLevel }));
  };

  const handleOrderClick = (key: keyof typeof order) => {
    const current = order[key];
    let next: "asc" | "desc" | "none";

    if (current === "none") next = "asc";
    else if (current === "asc") next = "desc";
    else next = "none";

    const newOrder = {
      [key]: next,
      ...Object.fromEntries(Object.entries(order).filter(([k]) => k !== key)),
    };
    // @ts-ignore
    setOrder(newOrder);
    dispatch(handlePaginate(1));
  };

  const handleDeleteItem = async (id: number) => {
    if (!canDelete) return;
    const result = await dispatch(deleteLoyaltyLevel(id ?? 0));
    if (result?.payload === true) {
      dispatch(
        showNotification({
          title: t("success"),
          message: t("entityDeleteSuccess"),
          type: NotificationTypes.success,
        }),
      );
    } else {
      dispatch(
        showNotification({
          title: t("error"),
          message: t("entityDeleteError"),
          type: NotificationTypes.error,
        }),
      );
    }
  };

  return (
    <div className="page-list-wrapper page-list--with-filter">
      <LevelsFilter
        handleApply={handleApply}
        searchValue={search}
        handleLevel={setLevel}
        levelValue={level}
        handleSearch={handleFilterSearch}
        selectedActive={active}
        handleUpdateActive={setActive}
      />
      <div className="page-list--with-filter-content">
        <div className="page-list-header">
          <div className="page-list-header--box">
            {canCreate && (
              <ActionBtn
                txt={t("addLevel")}
                handleClick={handleOpenCreateModal}
              />
            )}
          </div>
          <div className="page-list-header--box page-list-header--box--right">
            <RefreshButton
              position="right"
              disabled={isRefreshRequested}
              isLoading={isRefreshRequested}
              onClick={() => refresh()}
            />
          </div>
          <div className="page-list-header--box">
            <DefaultDropdown
              onSelect={(e) => {
                handleActionClick(e as string);
              }}
              placeholder={t("chooseAction")}
              options={options}
              isSmall
              disabled={selectedItems.length === 0 || canUpdate}
            />
          </div>
        </div>

        {!isListFetched && (
          <>
            <DataTableSkeleton columnsCount={8} rowsCount={4} />
            <PaginationSkeleton />
          </>
        )}

        {isListFetched && (
          <>
            <div className="game-table-wrap">
              {list.length === 0 && <EmptyState message={t("noLevelsFound")} />}

              {list.length > 0 && (
                <Table
                  data={list}
                  order={order}
                  idField="id"
                  columns={{
                    fixedColumns: [
                      "id",
                      "image",
                      "levelNumber",
                      "title",
                      "pointsRequired",
                      "valueToComplete",
                      "totalLevelUpAmount",
                      "rewards",
                      "locales",
                      "isActive",
                    ],
                    sortingColumns: {
                      pointsRequired: {
                        value: t("pointsRequired"),
                      },
                      totalLevelUpAmount: {
                        value: t("totalLevelUp"),
                        tooltip: t("totalLevelUpTooltip"),
                      },
                      valueToComplete: {
                        value: t("pointsTo"),
                        hasSorting: true,
                      },
                    },
                  }}
                  selectedItems={selectedItems}
                  hasCheckbox={canUpdate}
                  hasEdit={canUpdate}
                  hasView={canRead && !canUpdate}
                  hasDelete={canDelete}
                  hasOptions={canRead || canUpdate || canDelete}
                  customColumns={(row) => ({
                    rewards: <LoyaltyRewardTypeLabels rewards={row.rewards} />,
                  })}
                  onAllSelect={handleAllClick}
                  onSelect={(id) => handleItemClick(id)}
                  onDelete={(id) => handleDeleteItem(id)}
                  onEdit={
                    canUpdate ? (id) => handleOpenEditModal(id) : undefined
                  }
                  onView={
                    canRead && !canUpdate
                      ? (id) => handleOpenEditModal(id)
                      : undefined
                  }
                  onSort={(id) => (id ? handleOrderClick(id) : {})}
                  activeRowId={activeLevelId}
                  processingId={processingItemId}
                />
              )}
            </div>

            {list.length > 0 && (
              <Pagination
                handleLimitClick={(e) => {
                  setLimit(e);
                  dispatch(handlePaginate(1));
                }}
                limitsArray={loyaltyListLimits}
                activePage={activePage}
                limit={currentLimit}
                totalItems={totalItems}
                totalPages={totalPages}
                handleNextClick={handleNextClick}
                handlePrevClick={handlePrevClick}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default LevelsList;
